import { TTimeRanges } from "#next/utils/shared";
import { omit, pick } from "lodash";
import { z } from "zod";
import {
  QueryStringTimeRanges,
  QueryStringToTimeRange,
} from "#next/components/explorer/filters/TimeRangeSelector";
import { DateTime } from "luxon";

/* Narrative Filter */

export type TQueryStringNarrativeItem = {
  id: string;
  score: number;
};

// Projects are only allowed to have 3 narratives
export type TQueryStringNarrative = [
  TQueryStringNarrativeItem,
  TQueryStringNarrativeItem | null,
  TQueryStringNarrativeItem | null,
];

/* Feed Filter */

const feedFilterByQueryStringValues = ["p", "h", "l", "u", "ln", "e"] as const;
const feedFilterByQueryStringNames = [
  "platform",
  "hashtag",
  "link",
  "username",
  "language",
  "entity",
] as const;

type TFeedFilterByQueryStringValues =
  (typeof feedFilterByQueryStringValues)[number];

export type TFeedFilterByQueryStringNames =
  (typeof feedFilterByQueryStringNames)[number];

const feedFilterByMapQueryStringValueToName: Readonly<
  Record<TFeedFilterByQueryStringValues, TFeedFilterByQueryStringNames>
> = {
  p: "platform",
  h: "hashtag",
  l: "link",
  u: "username",
  e: "entity",
  ln: "language",
} as const;

export type TQueryStringFeedFilter =
  | {
      filterBy: TFeedFilterByQueryStringNames;
      value: string;
      date?: string;
    }
  | {
      filterBy: "language";
      value: TQueryStringLanguageTypeNames;
      date: string;
    };

const feedFilterByMapNameToQueryStringValue: Readonly<
  Record<TFeedFilterByQueryStringNames, TFeedFilterByQueryStringValues>
> = {
  hashtag: "h",
  link: "l",
  platform: "p",
  username: "u",
  language: "ln",
  entity: "e",
} as const;

export const queryStringLanguageNames = [
  "violent",
  "hateful",
  "negative",
  "offensive",
] as const;

export type LanguageNames = (typeof queryStringLanguageNames)[number];
export type TQueryStringLanguage = {
  [key in LanguageNames]: number | null;
} & { mode: TLanguageModeQueryStringValues };

export type TQueryStringLanguageTypeNames = Exclude<
  keyof TQueryStringLanguage,
  "mode"
>;

const languageModeQueryStringValues = ["and", "or"] as const;
export type TLanguageModeQueryStringValues =
  (typeof languageModeQueryStringValues)[number];

export type TQueryStringFilterSettings = {
  keywords: string[] | null;
  platforms: string[] | null;
  feed: TQueryStringFeedFilter | null;
  timeRange: TTimeRanges;
  language: TQueryStringLanguage;
  relevance: number | null;
  narrative: TQueryStringNarrative | null;
  mediaCategory: string | null;
  sort: (typeof SortFields)[number] | null;
  sortDirection: "asc" | "desc" | null;
  condenseText: boolean | null;
  latitude: number | null;
  longitude: number | null;
  dateReference: string | null;
};

export const SortFields = [
  "Date",
  "Username",
  "Relevance",
  "Violent",
  "Hateful",
  "Negative",
  "Offensive",
  "Engagement",
  "Occurrences",
] as const;

const FilterQueryStringKey = {
  keywords: "kw",
  platforms: "p",
  feed: {
    filterBy: "fby",
    value: "fv",
    date: "fd",
    language: "fl",
  },
  language: {
    violent: "lv",
    hateful: "lh",
    negative: "ln",
    offensive: "lo",
    mode: "lm",
  },
  relevance: "r",
  mediaCategory: "mc",
  timeRange: "tr",
  timeRangeCustom: "trc",
  narratives: [
    { id: "n1id", score: "n1s" },
    { id: "n2id", score: "n2s" },
    { id: "n3id", score: "n3s" },
  ],
  sort: "s",
  sortDirection: "d",
  condenseText: "cd",
  latitude: "lat",
  longitude: "lon",
  dateReference: "dr",
} as const;

const zString = z.string().min(1).optional().nullable().default(null);
const zNumber = z
  .string()
  .optional()
  .nullable()
  .default(null)
  .transform((s) => (s ? Number(s) : null));
const zUuid = z.string().uuid().optional().nullable().default(null);

const requireBothOrNeither = (
  val1: string | null | number,
  val2: string | null | number,
) => {
  return (val1 === null && val2 === null) || (val1 !== null && val2 !== null);
};

export const zQueryString = z
  .object({
    projectId: z.string().uuid().optional(),
    searchId: z.string().uuid().optional(),
    [FilterQueryStringKey.keywords]: z
      .union([z.string(), z.array(z.string().min(0))])
      .transform((value) => (Array.isArray(value) ? value : [value]))
      .optional()
      .nullable()
      .default(null),
    [FilterQueryStringKey.platforms]: z
      .union([z.string(), z.array(z.string().min(1))])
      .transform((value) => (Array.isArray(value) ? value : [value]))
      .optional()
      .nullable()
      .default(null),
    [FilterQueryStringKey.feed.filterBy]: z
      .enum(feedFilterByQueryStringValues)
      .optional()
      .nullable()
      .default(null),
    [FilterQueryStringKey.feed.value]: zString,
    [FilterQueryStringKey.feed.language]: zString,
    [FilterQueryStringKey.feed.date]: zString.refine((value) => {
      if (!value) return true;
      return (
        DateTime.fromFormat(value, "yyyy-MM-dd").invalidExplanation === null
      );
    }),
    [FilterQueryStringKey.timeRange]: z.enum(QueryStringTimeRanges),
    [FilterQueryStringKey.timeRangeCustom]: zString, // TODO validate dates
    [FilterQueryStringKey.language.hateful]: zNumber,
    [FilterQueryStringKey.language.violent]: zNumber,
    [FilterQueryStringKey.language.negative]: zNumber,
    [FilterQueryStringKey.language.offensive]: zNumber,
    [FilterQueryStringKey.language.mode]: z
      .enum(languageModeQueryStringValues)
      .optional()
      .nullable()
      .default(null),
    [FilterQueryStringKey.relevance]: zNumber,
    [FilterQueryStringKey.mediaCategory]: zString,
    [FilterQueryStringKey.narratives[0].id]: zUuid,
    [FilterQueryStringKey.narratives[0].score]: zNumber,
    [FilterQueryStringKey.narratives[1].id]: zUuid,
    [FilterQueryStringKey.narratives[1].score]: zNumber,
    [FilterQueryStringKey.narratives[2].id]: zUuid,
    [FilterQueryStringKey.narratives[2].score]: zNumber,
    [FilterQueryStringKey.sort]: z.enum(SortFields).optional().nullable(),
    [FilterQueryStringKey.sortDirection]: z
      .enum(["asc", "desc"])
      .optional()
      .nullable(),
    [FilterQueryStringKey.condenseText]: z
      .enum(["true", "false"])
      .transform((value) => value === "true")
      .optional()
      .nullable(),
    [FilterQueryStringKey.latitude]: zNumber,
    [FilterQueryStringKey.longitude]: zNumber,
    [FilterQueryStringKey.dateReference]: zString,
  })
  .strict()
  .superRefine((values, ctx) => {
    if (!!values.projectId && !!values.searchId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `either a project id or search id must be set`,
      });
    }
    const { fby, fv } = values;
    if (!requireBothOrNeither(fby, fv)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `feed filter by and feed filter value must either both be null or both be not null`,
      });
    }

    const { lh, ln, lo, lv, lm } = values;
    if ([lh, ln, lo, lv].some((item) => item !== null) && lm === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Language mode (${FilterQueryStringKey.language.mode}) must be in query string when any language filter is set`,
      });
    }

    const { n1id, n1s, n2id, n2s, n3id, n3s } = values;
    [
      [n1id, n1s],
      [n2id, n2s],
      [n3id, n3s],
    ].forEach(([id, score]) => {
      if (!requireBothOrNeither(id ?? null, score ?? null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Narratives require both id and score. Got id ${id} and score ${score}`,
        });
      }
    });
  })
  .transform((data): TQueryStringFilterSettings => {
    let feed: TQueryStringFeedFilter | null = null;
    if (data.fby && data.fv) {
      const filterBy = feedFilterByMapQueryStringValueToName[data.fby];
      if (filterBy === "language") {
        feed = {
          filterBy: "language",
          value: z.enum(queryStringLanguageNames).parse(data.fv),
        };
      } else {
        feed = {
          filterBy,
          value: data.fv,
        };
      }
      if (data.fd) {
        feed.date = data.fd;
      }
    }

    let narrative: TQueryStringNarrative | null = null;
    if (data.n1id && data.n1s) {
      narrative = [
        {
          id: data.n1id,
          score: data.n1s,
        },
        data.n2id && data.n2s
          ? {
              id: data.n2id,
              score: data.n2s,
            }
          : null,
        data.n3id && data.n3s
          ? {
              id: data.n3id,
              score: data.n3s,
            }
          : null,
      ];
    }

    return {
      keywords: data.kw,
      platforms: data.p,
      feed: feed,
      timeRange: QueryStringToTimeRange[data.tr],
      language: {
        violent: data.lv ? data.lv : null,
        hateful: data.lh ? data.lh : null,
        offensive: data.lo ? data.lo : null,
        negative: data.ln ? data.ln : null,
        mode: data.lm ?? "or",
      },
      relevance: data.r ? data.r : null,
      narrative,
      mediaCategory: data.mc,
      sort: data.s ?? null,
      sortDirection: data.d ?? null,
      condenseText: data.cd ?? null,
      latitude: data.lat ? data.lat : null,
      longitude: data.lon ? data.lon : null,
      dateReference: data.dr ?? null,
    };
  });

/* Surprisingly, url.searchParams.entries() doesn't seem to handle multiple
 values with the same key, so build a datastructure manually.
*/
function getSearchParams(searchParams: URLSearchParams) {
  const params: Record<string, string | string[]> = {};
  for (const key of searchParams.keys()) {
    const values = searchParams.getAll(key);
    if (values.length === 1) {
      const value = values[0];
      if (value) {
        params[key] = value;
      }
    } else {
      params[key] = values;
    }
  }
  return params;
}

const getCurrentSearchParams = () => {
  const url = new URL(window.location.href);
  return getSearchParams(url.searchParams);
};

/**
 UI components should not use this function, they should select the filters from the store
 Otherwise, the UI may not update when the filters are changed
 */
export const getFiltersFromQueryString = (
  filterStateFakeUrl: string | null,
): TQueryStringFilterSettings => {
  const url = new URL(filterStateFakeUrl ?? window.location.href);
  const searchParams = getSearchParams(url.searchParams);
  const parsed = zQueryString.safeParse(searchParams);
  if (parsed.success) {
    return parsed.data;
  } else {
    // Shouldn't be possible for the fake URL to be wrong, but it is possible
    // a user will enter a URL where part of the query string is invalid.
    // Don't break the application for the user because the query string was wrong,
    // just give them a default search with the project they tried to load.
    console.warn(
      "Error parsing Explorer URL, resetting to default search params",
      parsed.error,
      url,
    );
    const projectId = searchParams.projectId || searchParams.searchId;
    if (!projectId || Array.isArray(projectId)) {
      throw new Error(`projectId must be a string but got ${projectId}`);
    }
    const newFilters = {
      projectId,
      tr: "7d",
    };
    setQueryStringFilters(newFilters, filterStateFakeUrl);
    return zQueryString.parse(newFilters);
  }
};

const setQueryStringFilters = (
  query: ReturnType<typeof getSearchParams>,
  filterStateFakeUrl: string | null,
): string | null => {
  /* Using Next.js Router.push, even in shallow mode, will cause rerendering
  and refetching. So instead manage the query string directly. */

  const newUrl = filterStateFakeUrl
    ? new URL(filterStateFakeUrl)
    : new URL(window.location.href);
  Object.entries(query).forEach(([key, value]) => {
    if (!value) {
      newUrl.searchParams.delete(key);
    } else {
      if (Array.isArray(value)) {
        newUrl.searchParams.delete(key);
        value.forEach((v) => newUrl.searchParams.append(key, v));
      } else {
        newUrl.searchParams.set(key, value);
      }
    }
  });

  // Delete any keys that should no longer be in the query string
  const newUrlKeys = new Set(Object.keys(query));
  for (const currentKey of new URL(window.location.href).searchParams.keys()) {
    if (!newUrlKeys.has(currentKey)) {
      newUrl.searchParams.delete(currentKey);
    }
  }

  if (!filterStateFakeUrl) {
    history.pushState({}, "", newUrl);
    return null;
  }
  return newUrl.href;
};

export const setQueryStringNarrativeFilter = (
  narratives: TQueryStringFilterSettings["narrative"],
  filterStateFakeUrl: string | null,
) => {
  if (!narratives) {
    const query = omit(
      getCurrentSearchParams(),
      FilterQueryStringKey.narratives.flatMap((narr) => [narr.id, narr.score]),
    );
    return setQueryStringFilters(query, filterStateFakeUrl);
  }
  const [narrative1, narrative2, narrative3] = narratives;
  const [queryStringNarrative1, queryStringNarrative2, queryStringNarrative3] =
    FilterQueryStringKey.narratives;

  const updates: Record<string, string> = {
    [queryStringNarrative1.id]: narrative1.id,
    [queryStringNarrative1.score]: String(narrative1.score),
  };

  if (narrative2) {
    updates[queryStringNarrative2.id] = narrative2.id;
    updates[queryStringNarrative2.score] = String(narrative2.score);
  }

  if (narrative3) {
    updates[queryStringNarrative3.id] = narrative3.id;
    updates[queryStringNarrative3.score] = String(narrative3.score);
  }

  return setQueryStringFilters(
    {
      ...getCurrentSearchParams(),
      ...updates,
    },
    filterStateFakeUrl,
  );
};

export function setQueryStringLanguageFilter(
  language: TQueryStringLanguage,
  filterStateFakeUrl: string | null,
) {
  const query = { ...getCurrentSearchParams() };
  const languageKinds: Exclude<keyof TQueryStringLanguage, "mode">[] = [
    "hateful",
    "negative",
    "offensive",
    "violent",
  ];
  for (const languageKind of languageKinds) {
    const value = language[languageKind];
    const qsKey = FilterQueryStringKey.language[languageKind];
    if (value === null) {
      delete query[qsKey];
    } else {
      query[qsKey] = String(value);
    }
  }

  const mode = language["mode"];
  const modeQsKey = FilterQueryStringKey.language.mode;
  if (mode == null) {
    delete query[modeQsKey];
  } else {
    query[modeQsKey] = mode;
  }

  return setQueryStringFilters(query, filterStateFakeUrl);
}

export const setQueryStringRelevance = (
  relevance: TQueryStringFilterSettings["relevance"],
  filterStateFakeUrl: string | null,
) => {
  return updateQueryString(
    FilterQueryStringKey.relevance,
    String(relevance),
    filterStateFakeUrl,
  );
};

export const setQueryStringMediaCategory = (
  label: TQueryStringFilterSettings["mediaCategory"],
  filterStateFakeUrl: string | null,
) => {
  return updateQueryString(
    FilterQueryStringKey.mediaCategory,
    label,
    filterStateFakeUrl,
  );
};

export const setQueryStringSort = (
  sort: TQueryStringFilterSettings["sort"],
  filterStateFakeUrl: string | null,
) => {
  return updateQueryString(FilterQueryStringKey.sort, sort, filterStateFakeUrl);
};

export const setQueryStringSortDirection = (
  sortDirection: TQueryStringFilterSettings["sortDirection"],
  filterStateFakeUrl: string | null,
) => {
  return updateQueryString(
    FilterQueryStringKey.sortDirection,
    sortDirection,
    filterStateFakeUrl,
  );
};

export const setQueryStringDateReferenceFilter = (
  dateReference: TQueryStringFilterSettings["dateReference"],
  filterStateFakeUrl: string | null,
) => {
  return updateQueryString(
    FilterQueryStringKey.dateReference,
    dateReference,
    filterStateFakeUrl,
  );
};

export const clearQueryStringDateReferenceFilter = (
  filterStateFakeUrl: string | null,
) => {
  return setQueryStringFilters(
    omit(getCurrentSearchParams(), [FilterQueryStringKey.dateReference]),
    filterStateFakeUrl,
  );
};

export const setQueryStringCondenseText = (
  condenseText: TQueryStringFilterSettings["condenseText"],
  filterStateFakeUrl: string | null,
) => {
  return updateQueryString(
    FilterQueryStringKey.condenseText,
    condenseText,
    filterStateFakeUrl,
  );
};

export const setQueryStringKeywordFilter = (
  keywords: TQueryStringFilterSettings["keywords"],
  filterStateFakeUrl: string | null,
) =>
  updateQueryString(
    FilterQueryStringKey.keywords,
    keywords,
    filterStateFakeUrl,
  );

export const setQueryStringPlatformsFilter = (
  platforms: TQueryStringFilterSettings["platforms"],
  filterStateFakeUrl: string | null,
) =>
  updateQueryString(
    FilterQueryStringKey.platforms,
    platforms,
    filterStateFakeUrl,
  );

export function setQueryStringFeedFilter(
  { filterBy, value, date }: TQueryStringFeedFilter,
  filterStateFakeUrl: string | null,
) {
  let newFilters: Record<string, string> = {
    ...getCurrentSearchParams(),
    [FilterQueryStringKey.feed.filterBy]:
      feedFilterByMapNameToQueryStringValue[filterBy],
    [FilterQueryStringKey.feed.value]: value,
  };
  if (date) {
    // There's a problem here: date is created in a timezone (the user's local TZ currently)
    // but if the link is shared to someone else, the date will be interpreted in their TZ,
    // so they will probably see different results. Possibly we should put the epoch millis
    // in the URL instead?
    newFilters = { ...newFilters, [FilterQueryStringKey.feed.date]: date };
  }
  return setQueryStringFilters(newFilters, filterStateFakeUrl);
}

export const clearSecondaryQueryStringFeedFilter = (
  filterStateFakeUrl: string | null,
) =>
  setQueryStringFilters(
    omit(getCurrentSearchParams(), [
      FilterQueryStringKey.feed.filterBy,
      FilterQueryStringKey.feed.value,
      FilterQueryStringKey.feed.date,
    ]),
    filterStateFakeUrl,
  );

export const clearQueryStringLocationFilter = (
  filterStateFakeUrl: string | null,
) => {
  return setQueryStringFilters(
    omit(getCurrentSearchParams(), [
      FilterQueryStringKey.latitude,
      FilterQueryStringKey.longitude,
    ]),
    filterStateFakeUrl,
  );
};

export const setQueryStringLocationFilter = (
  filterStateFakeUrl: string | null,
  latitude: string,
  longitude: string,
) => {
  const newFilters: Record<string, string> = {
    ...getCurrentSearchParams(),
    [FilterQueryStringKey.latitude]: latitude,
    [FilterQueryStringKey.longitude]: longitude,
  };
  return setQueryStringFilters(newFilters, filterStateFakeUrl);
};

export const clearQueryStringFeedFilter = (
  filterStateFakeUrl: string | null,
) => {
  return setQueryStringFilters(
    pick(getCurrentSearchParams(), [
      "projectId",
      "searchId",
      FilterQueryStringKey.timeRange,
    ]),
    filterStateFakeUrl,
  );
};

function updateQueryString(
  key: string,
  value: string | string[] | boolean | null,
  filterStateFakeUrl: string | null,
) {
  const query = value
    ? { ...getCurrentSearchParams(), [key]: value }
    : omit(getCurrentSearchParams(), key);
  return setQueryStringFilters(query, filterStateFakeUrl);
}
